import React from 'react';
import {
  MapPin,
  PenTool,
  Map,
  ClipboardCheck,
  LayoutDashboard,
  BarChart2,
  Settings,
  FileSearch,
  Users,
  BrainCircuit,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// localStorage keys
// ─────────────────────────────────────────────────────────────────────────────
export const LAUNCH_TOKEN_KEY     = 'launch_token';
export const LAUNCH_GOOGLE_ID_KEY = 'launch_googleId';
export const LAUNCH_PROFILE_KEY   = 'launch_profile';
export const LAUNCH_EXPIRY_KEY    = 'launch_expiry';

// Google access tokens expiran en 1 hora
export const LAUNCH_SESSION_TTL_MS = 60 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Internal module definition
// ─────────────────────────────────────────────────────────────────────────────
export interface InternalModule {
  id: string;
  name: string;
  description: string;
  summary: string;
  bullets: string[];
  chipColors?: string[];
  path: string;
  icon: React.ElementType;
  color: string;
}

export const MODULE_GEORREF: InternalModule = {
  id: 'georref',
  name: 'Corrección de datos',
  description: 'Validación de datos y coordenadas',
  summary: 'Cuando el GPS de un inmueble no coincide con su dirección registrada, aquí puedes revisarlo y corregirlo directamente sobre el mapa.',
  bullets: ['Ver en mapa', 'Validar GPS', 'Historial de cambios'],
  path: '/document-audit/georeferencing',
  icon: MapPin,
  color: '#0ea5e9',
};

export const MODULE_POLYNATOR: InternalModule = {
  id: 'polynator',
  name: 'Polynator',
  description: 'Creación y edición de polígonos',
  summary: 'Dibuja y gestiona zonas geográficas sobre el mapa. Ideal para definir coberturas de servicio, editar límites y detectar intersecciones entre zonas.',
  bullets: ['Dibujar zona', 'Editar límites', 'Ver intersecciones'],
  path: '/polygons/dashboard-tools',
  icon: PenTool,
  color: '#7c3aed',
};

export const MODULE_POLYGON_VIEWER: InternalModule = {
  id: 'polygon-viewer',
  name: 'Visor de polígonos',
  description: 'Visualización de polígonos',
  summary: 'Consulta todas las zonas activas en el mapa sin riesgo de modificarlas. Perfecto para auditar coberturas o verificar límites antes de tomar decisiones.',
  bullets: ['Explorar zonas', 'Consultar cobertura', 'Solo lectura'],
  path: '/polygon-viewer/polygon-viewer-dashboard',
  icon: Map,
  color: '#7c3aed',
};

export const MODULE_DOC_REVIEW: InternalModule = {
  id: 'doc-review',
  name: 'Revisión de docs',
  description: 'Auditoría y revisión de documentos',
  summary: 'Cola de documentos pendientes de revisión. Aprueba o rechaza según el flujo del orquestador, con sugerencias automáticas para agilizar el proceso.',
  bullets: ['Revisar cola', 'Aprobar / Rechazar', 'Ver historial'],
  path: '/document-audit/tasks',
  icon: ClipboardCheck,
  color: '#f59e0b',
};

export const MODULE_LA_CENTRAL: InternalModule = {
  id: 'la-central',
  name: 'La central',
  description: 'Comité de precios y orquestación',
  summary: 'Sigue el avance de cada inmueble dentro del proceso de precios. Busca por NID y consulta en qué paso está, qué falta y cuál es su estado actual.',
  bullets: ['Ver pipeline', 'Buscar NID', 'Estado del proceso'],
  path: '/orchestrator-pricing-committee/list-opc',
  icon: LayoutDashboard,
  color: '#10b981',
};

export const MODULE_AT_CONSOLE: InternalModule = {
  id: 'at-console',
  name: 'AT Console',
  description: 'Algorithmic Trading · Motor de decisiones',
  summary: 'Plataforma de observabilidad y control del motor de pricing. Agrupa dos consolas independientes: HeSH para decisiones en tiempo real y Pricing Committee para trazabilidad del pipeline de valoración.',
  bullets: ['HeSH · Decisiones live', 'Pricing Committee · Pipeline'],
  chipColors: ['#10b981', '#f97316'],
  path: '/algorithmic-trading',
  icon: BrainCircuit,
  color: '#10b981',
};

// ─── Módulos dummy para pruebas visuales ─────────────────────────────────────
export const MODULE_DUMMY_1: InternalModule = {
  id: 'dummy-analytics',
  name: 'Analytics',
  description: 'Reportes y métricas de negocio',
  summary: 'Visualiza reportes y métricas clave del negocio.',
  bullets: ['Ver reportes', 'Exportar datos', 'Filtrar por período'],
  path: '/',
  icon: BarChart2,
  color: '#f59e0b',
};

export const MODULE_DUMMY_2: InternalModule = {
  id: 'dummy-config',
  name: 'Configuración',
  description: 'Parámetros y ajustes del sistema',
  summary: 'Ajusta los parámetros y preferencias del sistema.',
  bullets: ['Editar parámetros', 'Gestionar permisos', 'Ver logs'],
  path: '/',
  icon: Settings,
  color: '#6b7280',
};

export const MODULE_DUMMY_3: InternalModule = {
  id: 'dummy-audit',
  name: 'Auditoría',
  description: 'Historial y trazabilidad de cambios',
  summary: 'Consulta el historial completo de cambios y acciones del sistema.',
  bullets: ['Ver historial', 'Filtrar por usuario', 'Exportar trazabilidad'],
  path: '/',
  icon: FileSearch,
  color: '#8b5cf6',
};

export const MODULE_DUMMY_4: InternalModule = {
  id: 'dummy-users',
  name: 'Usuarios',
  description: 'Gestión de accesos y perfiles',
  summary: 'Administra quién tiene acceso y a qué módulos dentro de la plataforma.',
  bullets: ['Invitar usuario', 'Asignar roles', 'Ver accesos'],
  path: '/',
  icon: Users,
  color: '#ec4899',
};

// ─────────────────────────────────────────────────────────────────────────────
// Panel height constants
// ListItem: icon 36px + padding 22px + border 1px = 59px
// PanelHeader ~78px | PanelFooter ~43px
// ─────────────────────────────────────────────────────────────────────────────
export const MAX_COLLAPSED        = 5;
export const MAX_EXPANDED         = 8;
export const ITEM_HEIGHT          = 59;
export const PANEL_HEADER_HEIGHT  = 78;
export const PANEL_FOOTER_HEIGHT  = 43;
export const PANEL_HEIGHT         = PANEL_HEADER_HEIGHT + MAX_COLLAPSED * ITEM_HEIGHT + PANEL_FOOTER_HEIGHT; // 416px
export const EXPANDED_HEIGHT      = PANEL_HEADER_HEIGHT + MAX_EXPANDED  * ITEM_HEIGHT + PANEL_FOOTER_HEIGHT; // 593px

// ─────────────────────────────────────────────────────────────────────────────
// Environment display config
// ─────────────────────────────────────────────────────────────────────────────
export const ENV_LABELS: Record<string, string> = {
  local: 'Localhost',
  qa:    'QA (.llavero.co)',
  prod:  'Producción (.habi.co)',
};

export const ENV_COLORS: Record<string, string> = {
  local: '#f59e0b',
  qa:    '#3b82f6',
  prod:  '#10b981',
};
